import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
} from "@tanstack/react-query";
import { tuyau } from "@/config/tuyau";
import { citiesCoords } from "@/domain/chef/chef.constant";
import type {
  BannerFormData,
  ChefInformationFormData,
  LocationFormData,
  PersonalInformationFormData,
} from "@/domain/chef/chef.schema";
import type { ChefSearchParams } from "@/domain/chef/chef.type";

export const chefKeys = {
  all: ["chef"] as const,
  me: () => [...chefKeys.all, "me"] as const,
  profile: (chefId: number) => [...chefKeys.all, "profile", chefId] as const,
  aroundCity: () => [...chefKeys.all, "around-city"] as const,
  findChefs: (searchParams: ChefSearchParams) =>
    [...chefKeys.all, "find-chefs", searchParams] as const,
  chefsFirstPage: (searchParams: ChefSearchParams) =>
    [...chefKeys.all, "chefs-first-page", searchParams] as const,
  adminChefs: () => [...chefKeys.all, "admin-chefs"] as const,
  dashboard: () => [...chefKeys.all, "dashboard"] as const,
  recentFeedbacks: () => [...chefKeys.all, "recent-feedbacks"] as const,
};

export const chefQueries = {
  me: () =>
    queryOptions({
      queryKey: chefKeys.me(),
      queryFn: async () => (await tuyau.chefs.me({})).data,
    }),

  profile: (chefId: number) =>
    queryOptions({
      queryKey: chefKeys.profile(chefId),
      queryFn: async () =>
        (
          await tuyau.chefs.show({
            params: { id: chefId },
            query: { id: chefId },
          })
        ).data,
    }),

  aroundCity: () =>
    queryOptions({
      queryKey: chefKeys.aroundCity(),
      queryFn: async () =>
        (await tuyau.chefs.availableByCity({ body: citiesCoords })).data,
    }),

  chefsFirstPage: (searchParams: ChefSearchParams) =>
    queryOptions({
      queryKey: chefKeys.chefsFirstPage(searchParams),
      queryFn: async () =>
        await tuyau.chefs.index({
          query: {
            ...searchParams,
            page: 1,
          },
        }),
    }),

  findChefs: (searchParams: ChefSearchParams) =>
    infiniteQueryOptions({
      queryKey: chefKeys.findChefs(searchParams),
      queryFn: async ({ pageParam = 1 }) => {
        const response = await tuyau.chefs.index({
          query: {
            ...searchParams,
            page: pageParam,
          },
        });

        return response;
      },
      getNextPageParam: (lastPage: any) => {
        if (!lastPage.metadata.nextPageUrl) return null;
        return lastPage.metadata.currentPage + 1;
      },
      initialPageParam: 1,
    }),

  adminChefs: () =>
    queryOptions({
      queryKey: chefKeys.adminChefs(),
      queryFn: async () => await tuyau.chefs.adminIndex({}),
    }),

  dashboard: () =>
    queryOptions({
      queryKey: chefKeys.dashboard(),
      queryFn: async () => await tuyau.chefs.getDashboardData({}),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 2,
    }),

  recentFeedbacks: () =>
    queryOptions({
      queryKey: chefKeys.recentFeedbacks(),
      queryFn: async () => await tuyau.chefs.myFeedbacks({}),
      staleTime: 5 * 60 * 1000,
    }),

  publicFeedbacks: (chefId: number) =>
    queryOptions({
      queryKey: [...chefKeys.all, "public-feedbacks", chefId] as const,
      queryFn: async () =>
        await tuyau.chefs.publicFeedbacks({ params: { id: chefId } }),
      staleTime: 5 * 60 * 1000,
      enabled: chefId > 0,
    }),
};

export const chefMutations = {
  updatePersonalInformation: () =>
    mutationOptions({
      mutationFn: async (data: PersonalInformationFormData) =>
        await tuyau.chefs.updatePersonalInformation({
          body: data as never,
        }),
    }),

  updateChefInformation: () =>
    mutationOptions({
      mutationFn: async (data: ChefInformationFormData) =>
        await tuyau.chefs.updateChefInformation({
          body: data,
        }),
    }),

  updateChefLocation: () =>
    mutationOptions({
      mutationFn: async (data: LocationFormData) =>
        await tuyau.chefs.updateChefLocation({
          body: data,
        }),
    }),

  updateChefBanner: () =>
    mutationOptions({
      mutationFn: async ({ banner }: BannerFormData) =>
        await tuyau.chefs.updateChefBanner({
          body: {
            banner,
          } as never,
        }),
    }),

  validateProfile: () =>
    mutationOptions({
      mutationFn: async ({ chefId }: { chefId: number }) =>
        await tuyau.chefs.validate({ params: { chefId } }),
    }),

  lockProfile: () =>
    mutationOptions({
      mutationFn: async ({ chefId }: { chefId: number }) =>
        await tuyau.chefs.lock({ params: { chefId } }),
    }),

  requestDocuments: () =>
    mutationOptions({
      mutationFn: async ({ chefId }: { chefId: number }) =>
        await tuyau.chefs.requestDocuments({ params: { chefId } }),
    }),

  generateStripeConnectLink: () =>
    mutationOptions({
      mutationFn: async ({ chefId }: { chefId: number }) =>
        await tuyau.chefs.createStripeConnectAccount({ params: { chefId } }),
    }),
};
